import { createFileRoute, notFound } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DocumentViewer,
  OverlayLayer,
  type OverlayItemModel,
} from '@/components/docfiller/viewer'

/**
 * Dev-only preview for the document viewer's annotation/overlay layer.
 *
 * Renders a synthetic single-image document with a handful of overlay
 * rectangles spanning every kind (highlight, field, edit-target,
 * annotation) so the desktop + mobile experience can be smoke-tested
 * without uploading a real document. Reachable at `/dev/viewer-overlays`
 * in dev builds only — production 404s the route, identical guard
 * pattern to `dev.design-system.tsx`.
 */
export const Route = createFileRoute('/dev/viewer-overlays')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound()
  },
  component: ViewerOverlaysPreview,
})

// 800×600 PNG-like data URI rendered server-side at build time would
// pull in extra tooling; an inline SVG with a fake "form" works just
// as well for layout smoke and ships zero new deps.
const SAMPLE_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
  <rect width="800" height="600" fill="#fafafa"/>
  <text x="40" y="60" font-family="Inter, sans-serif" font-size="22" font-weight="600" fill="#111">
    Sample form
  </text>
  <text x="40" y="98" font-family="Inter, sans-serif" font-size="14" fill="#555">
    Overlay primitives smoke surface
  </text>
  <line x1="40" y1="140" x2="760" y2="140" stroke="#ddd" stroke-width="1" />
  <text x="40" y="180" font-family="Inter, sans-serif" font-size="13" fill="#444">
    First name
  </text>
  <rect x="40" y="195" width="320" height="40" stroke="#bbb" fill="#fff" />
  <text x="400" y="180" font-family="Inter, sans-serif" font-size="13" fill="#444">
    Last name
  </text>
  <rect x="400" y="195" width="320" height="40" stroke="#bbb" fill="#fff" />
  <text x="40" y="280" font-family="Inter, sans-serif" font-size="13" fill="#444">
    Email
  </text>
  <rect x="40" y="295" width="680" height="40" stroke="#bbb" fill="#fff" />
  <text x="40" y="380" font-family="Inter, sans-serif" font-size="13" fill="#444">
    Notes
  </text>
  <rect x="40" y="395" width="680" height="120" stroke="#bbb" fill="#fff" />
</svg>
`.trim()

const SAMPLE_URL = `data:image/svg+xml;utf8,${encodeURIComponent(SAMPLE_SVG)}`

const SAMPLE_OVERLAYS: OverlayItemModel[] = [
  {
    id: 'highlight-name',
    pageIndex: 0,
    rect: { x: 40, y: 160, width: 120, height: 18 },
    kind: 'highlight',
    label: 'AI-detected',
    description: 'Phrase the agent matched.',
    tone: 'warning',
  },
  {
    id: 'field-first-name',
    pageIndex: 0,
    rect: { x: 40, y: 195, width: 320, height: 40 },
    kind: 'field',
    label: 'First name',
    description: 'Click or press Enter to edit.',
  },
  {
    id: 'field-last-name',
    pageIndex: 0,
    rect: { x: 400, y: 195, width: 320, height: 40 },
    kind: 'field',
    label: 'Last name',
  },
  {
    id: 'edit-email',
    pageIndex: 0,
    rect: { x: 40, y: 295, width: 680, height: 40 },
    kind: 'edit-target',
    label: 'Email (being filled)',
    tone: 'info',
  },
  {
    id: 'annotation-notes',
    pageIndex: 0,
    rect: { x: 40, y: 395, width: 680, height: 120 },
    kind: 'annotation',
    label: 'Reviewer note',
    description: 'Sign here once the autofill completes.',
  },
]

function ViewerOverlaysPreview() {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activatedId, setActivatedId] = useState<string | null>(null)
  const [readOnly, setReadOnly] = useState(false)

  const source = useMemo(
    () => ({ id: 'sample', url: SAMPLE_URL, mimeType: 'image/svg+xml' }),
    [],
  )

  // `image/svg+xml` is treated as an image by the viewer; we keep this
  // page in dev only so we don't have to harden the production loader
  // against arbitrary SVG payloads.
  return (
    <div className="flex h-screen flex-col bg-background">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <h1 className="text-base font-semibold">Viewer overlays preview</h1>
          <p className="text-xs text-muted-foreground">
            Click an overlay or use Tab + arrow keys to navigate. Press
            Enter / double-click to activate a field overlay.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span data-testid="dev-selected-id" className="text-muted-foreground">
            Selected: {selectedId ?? '—'}
          </span>
          <span data-testid="dev-activated-id" className="text-muted-foreground">
            Activated: {activatedId ?? '—'}
          </span>
          <Button
            variant={readOnly ? 'soft' : 'outline'}
            size="sm"
            onClick={() => setReadOnly((v) => !v)}
            data-testid="dev-readonly-toggle"
          >
            {readOnly ? 'Read-only' : 'Interactive'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border px-4 py-2 text-xs">
        <Badge tone="warning" size="xs">
          highlight
        </Badge>
        <Badge tone="primary" size="xs">
          field
        </Badge>
        <Badge tone="info" size="xs">
          edit-target
        </Badge>
        <Badge tone="neutral" size="xs">
          annotation
        </Badge>
      </div>

      <div className="min-h-0 flex-1 p-4">
        <DocumentViewer
          source={source}
          autoFocus
          renderOverlay={({ page, scale, rotation }) => (
            <OverlayLayer
              pageIndex={page.index}
              pageDimensions={page.intrinsic}
              scale={scale}
              rotation={rotation}
              items={SAMPLE_OVERLAYS}
              selectedId={selectedId}
              readOnly={readOnly}
              onSelect={(event) => setSelectedId(event.id)}
              onActivate={(event) => setActivatedId(event.id)}
            />
          )}
        />
      </div>
    </div>
  )
}
